import './modal.scss'
import { useLogger } from '@omnia/ui/src/lib/hooks/useLogger'
import { setContinueAsGuest } from '@omnia/ui/src/lib/lumos-plus/storage/browser-session/actions/set-continue-as-guest'
import type { Showtime } from '@vista-digital/ocapi-types/v1/types/browsing/master-data/showtimes/showtime'
import { getMemberClubMembership } from '@vista-digital/occ-domain/src/lib/domains/loyalty/selectors/member/get-member-club-membership'
import { isMemberSignedIn } from '@vista-digital/occ-domain/src/lib/domains/loyalty/selectors/member/is-member-signed-in'
import { observer } from 'mobx-react'
import type { ForwardedRef } from 'react'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react'
import { BalanceModal } from './subcomponents/BalanceModal'
import { SignInModal } from './subcomponents/SignInModal'

const ComponentName = 'Modal'

type ModalType = 'sign-in' | 'balance' | null
type NavigationResolver = (shouldNavigate: boolean) => void
type PendingFlowType = 'showtime' | 'header-food-drink' | null

export interface ModalHandlers {
    handleShowtimeIntercept: (showtime: Showtime) => boolean | Promise<boolean>
    handleHeaderNavigationIntercept: (url: string) => boolean | Promise<boolean>
    handleCloseSignInModal: VoidFunction
}

interface ModalProps {
    isMembershipModalFlowEnabled: boolean
}

// Module-level flow state survives component remounts (EG. Login Flow).
let pendingShowtime: Showtime | null = null
let pendingHeaderUrl: string | null = null
let pendingFlowType: PendingFlowType = null
let pendingResolver: NavigationResolver | null = null
let shouldOpenBalanceAfterSignIn = false
let reopenBalanceModal: (() => void) | null = null

const getCurrentMemberState = () => {
    const membership = getMemberClubMembership()

    return {
        isSignedIn: isMemberSignedIn(),
        isBasicRole: membership?.clubLevelId === 3,
        membership,
    }
}

const ModalComponent = forwardRef(({ isMembershipModalFlowEnabled }: ModalProps, ref: ForwardedRef<ModalHandlers>) => {
    const logger = useLogger(ComponentName)
    const [activeModalType, setActiveModalType] = useState<ModalType>(null)

    const { isSignedIn, isBasicRole, membership } = getCurrentMemberState()

    const resolveNavigation = useCallback((shouldNavigate: boolean) => {
        pendingResolver?.(shouldNavigate)
        pendingResolver = null
    }, [])

    const resetFlow = useCallback(() => {
        setActiveModalType(null)
        pendingShowtime = null
        pendingHeaderUrl = null
        pendingFlowType = null
        pendingResolver = null
        shouldOpenBalanceAfterSignIn = false
    }, [])

    const handleCloseSignInModal = useCallback(() => {
        logger.info('Closing popup', {
            activeModalType,
            pendingShowtime,
            pendingHeaderUrl,
            pendingFlowType,
        })

        resolveNavigation(false)
        resetFlow()
    }, [activeModalType, logger, resolveNavigation, resetFlow])

    const handleAfterSuccessfulSignIn = useCallback(async () => {
        // Ensures that sign-in state updates are applied before we access them,
        // so we read the latest state instead of stale values.
        await Promise.resolve()

        const currentMemberState = getCurrentMemberState()

        logger.info('Successful sign in in modal flow', {
            pendingShowtime,
            pendingHeaderUrl,
            pendingFlowType,
            ...currentMemberState,
        })

        if (currentMemberState.isBasicRole) {
            resolveNavigation(true)
            resetFlow()
            return
        }

        shouldOpenBalanceAfterSignIn = true
        reopenBalanceModal?.()
    }, [logger, resolveNavigation, resetFlow])

    const handleBalanceContinue = useCallback(() => {
        logger.info('Continuing from balance modal', {
            pendingShowtime,
            pendingHeaderUrl,
            pendingFlowType,
        })

        resolveNavigation(true)
        resetFlow()
    }, [logger, resolveNavigation, resetFlow])

    const handleContinueAsGuestButtonClicked = useCallback(() => {
        logger.info('Continuing as guest in modal flow', {
            pendingShowtime,
            pendingHeaderUrl,
            pendingFlowType,
        })

        setContinueAsGuest(true)
        resolveNavigation(true)
        resetFlow()
    }, [logger, resolveNavigation, resetFlow])

    const handleShowtimeIntercept = useCallback(
        (showtime: Showtime) => {
            if (!isMembershipModalFlowEnabled) {
                logger.info('Membership modal flow disabled, using default navigation', {
                    showtime,
                })

                return true
            }

            pendingShowtime = showtime
            pendingHeaderUrl = null
            pendingFlowType = 'showtime'

            logger.info('Intercepted showtime click', {
                showtime,
                isSignedIn,
                isBasicRole,
                membership,
            })

            if (isSignedIn) {
                if (isBasicRole) {
                    return true
                }
                setActiveModalType('balance')

                return new Promise<boolean>(resolve => {
                    pendingResolver = resolve
                })
            }

            setActiveModalType('sign-in')

            return new Promise<boolean>(resolve => {
                pendingResolver = resolve
            })
        },
        [logger, isMembershipModalFlowEnabled, isSignedIn, isBasicRole, membership]
    )

    const handleHeaderNavigationIntercept = useCallback(
        (url: string) => {
            if (!isMembershipModalFlowEnabled) {
                logger.info('Modal flow disabled, using default navigation', {
                    url,
                })

                return true
            }

            pendingShowtime = null
            pendingHeaderUrl = url
            pendingFlowType = 'header-food-drink'

            logger.info('Intercepted food and drink header click', {
                url,
                isSignedIn,
                isBasicRole,
                membership,
            })

            if (isSignedIn) {
                if (isBasicRole) {
                    return true
                }

                setActiveModalType('balance')

                return new Promise<boolean>(resolve => {
                    pendingResolver = resolve
                })
            }

            setActiveModalType('sign-in')

            return new Promise<boolean>(resolve => {
                pendingResolver = resolve
            })
        },
        [logger, isMembershipModalFlowEnabled, isSignedIn, isBasicRole, membership]
    )

    useImperativeHandle(
        ref,
        () => ({
            handleShowtimeIntercept,
            handleHeaderNavigationIntercept,
            handleCloseSignInModal,
        }),
        [handleCloseSignInModal, handleShowtimeIntercept, handleHeaderNavigationIntercept]
    )

    useEffect(() => {
        if (!isMembershipModalFlowEnabled) {
            return
        }

        reopenBalanceModal = () => {
            logger.info('Opening balance modal through notifier', {
                pendingShowtime,
                pendingHeaderUrl,
                pendingFlowType,
            })

            setActiveModalType('balance')
            shouldOpenBalanceAfterSignIn = false
        }

        if (shouldOpenBalanceAfterSignIn) {
            reopenBalanceModal()
        }

        return () => {
            if (reopenBalanceModal) {
                reopenBalanceModal = null
            }
        }
    }, [logger, isMembershipModalFlowEnabled])

    switch (activeModalType) {
        case 'sign-in':
            return (
                <SignInModal
                    onClose={handleCloseSignInModal}
                    onAfterSuccessfulSignIn={handleAfterSuccessfulSignIn}
                    onContinueAsGuestButtonClicked={handleContinueAsGuestButtonClicked}
                />
            )

        case 'balance':
            return <BalanceModal onClose={handleCloseSignInModal} onContinue={handleBalanceContinue} />

        default:
            return null
    }
})

ModalComponent.displayName = ComponentName

export const Modal = observer(ModalComponent)
